import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { ShoppingCart, Menu, X, Phone, Heart, PackageSearch } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { usePicks } from '../../context/PicksContext';
import { badgePop } from '../../animations/variants';
import { openTrackOrder } from '../orders/TrackOrderModal';

const navLinks = [
  { label: 'Compression', href: '#compression' },
  { label: 'Braces', href: '#braces' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Contact', href: '#contact' },
];

export function Navbar() {
  const { totalItems, openCart, closeCart } = useCart();
  const { totalPicks, openPicks, closePicks } = usePicks();
  const handleOpenCart = () => { closePicks(); openCart(); };
  const handleOpenPicks = () => { closeCart(); openPicks(); };
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();

  useEffect(() => {
    const unsub = scrollY.on('change', (y) => setScrolled(y > 20));
    return unsub;
  }, [scrollY]);

  const bg = useTransform(scrollY, [0, 60], ['rgba(26,58,92,1)', 'rgba(255,255,255,1)']);
  const shadow = useTransform(scrollY, [0, 60], ['0 0 0 rgba(26,58,92,0)', '0 2px 20px rgba(26,58,92,0.12)']);

  return (
    <motion.nav
      style={{ backgroundColor: bg, boxShadow: shadow }}
      className="fixed top-0 left-0 right-0 z-40 border-b border-transparent transition-colors duration-300"
    >
      <div className="container-max section-padding">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <motion.a
            href="#"
            className="flex items-center gap-2.5 group"
            whileHover={{ scale: 1.02 }}
          >
            <div className="w-9 h-9 bg-ndms-navy rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow">
              <Heart size={18} className="text-white fill-white" />
            </div>
            <div>
              <div className={`font-bold text-base leading-tight transition-colors ${scrolled ? 'text-ndms-navy' : 'text-white'}`}>
                Northdale
              </div>
              <div className={`text-xs leading-tight font-medium transition-colors ${scrolled ? 'text-gray-500' : 'text-white/70'}`}>Medical Supplies</div>
            </div>
          </motion.a>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${scrolled ? 'text-gray-700 hover:text-ndms-navy hover:bg-ndms-navy/5' : 'text-white hover:text-white/80 hover:bg-white/10'}`}
              >
                {link.label}
              </a>
            ))}
            <button
              onClick={openTrackOrder}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${scrolled ? 'text-gray-700 hover:text-ndms-navy hover:bg-ndms-navy/5' : 'text-white hover:text-white/80 hover:bg-white/10'}`}
            >
              Track Order
            </button>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <a
              href="tel:+13069794115"
              className={`hidden md:flex items-center gap-1.5 text-sm transition-colors px-3 py-2 rounded-lg ${scrolled ? 'text-gray-600 hover:text-ndms-navy hover:bg-ndms-navy/5' : 'text-white hover:text-white/80 hover:bg-white/10'}`}
            >
              <Phone size={15} />
              <span className="font-medium">Call Us</span>
            </a>

            <motion.button
              onClick={handleOpenPicks}
              className={`relative p-2.5 rounded-xl transition-colors ${scrolled ? 'text-ndms-navy hover:bg-ndms-navy/10' : 'text-white hover:bg-white/20'}`}
              whileTap={{ scale: 0.92 }}
              aria-label={`Open My Picks – ${totalPicks} items`}
              title="My Picks"
            >
              <Heart size={22} />
              <AnimatePresence>
                {totalPicks > 0 && (
                  <motion.span
                    key={totalPicks}
                    variants={badgePop}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="absolute -top-1 -right-1 bg-[#7B68CC] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center"
                  >
                    {totalPicks > 99 ? '99+' : totalPicks}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>

            <motion.button
              onClick={handleOpenCart}
              className={`relative p-2.5 rounded-xl transition-colors ${scrolled ? 'text-ndms-navy hover:bg-ndms-navy/10' : 'text-white hover:bg-white/20'}`}
              whileTap={{ scale: 0.92 }}
              aria-label={`Open cart – ${totalItems} items`}
            >
              <ShoppingCart size={22} />
              <AnimatePresence>
                {totalItems > 0 && (
                  <motion.span
                    key={totalItems}
                    variants={badgePop}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="absolute -top-1 -right-1 bg-ndms-blue text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center"
                  >
                    {totalItems > 99 ? '99+' : totalItems}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>

            <button
              className={`md:hidden p-2.5 rounded-xl transition-colors ${scrolled ? 'text-ndms-navy hover:bg-ndms-navy/10' : 'text-white hover:bg-white/20'}`}
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="md:hidden bg-white border-t border-gray-100 overflow-hidden"
          >
            <div className="section-padding py-3 flex flex-col gap-1">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setMenuOpen(false)}
                  className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-ndms-navy hover:bg-ndms-navy/5 rounded-lg transition-all"
                >
                  {link.label}
                </a>
              ))}
              <button
                onClick={() => { setMenuOpen(false); openTrackOrder(); }}
                className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 hover:text-ndms-navy hover:bg-ndms-navy/5 rounded-lg transition-all text-left"
              >
                <PackageSearch size={15} />
                Track Order
              </button>
              <a
                href="tel:+13069794115"
                className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-ndms-navy hover:bg-ndms-navy/5 rounded-lg transition-all"
              >
                <Phone size={15} />
                Call Us
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
